import { Injectable } from '@angular/core';
import { Action, State, StateContext, Store } from '@ngxs/store';
import { map, switchMap, tap } from 'rxjs/operators';
import { ApiService } from '../api/api.service';
import { COOKIE_DEV, COOKIE_OBSSO, COOKIE_SESSION } from '../constants';
import { StorageService } from '../storage/storage.service';
import { decodeCookie, encodeCookie, encodeCookieNew, serializeFromObjectToString } from '../util.class';
import { CookieAuth, Login, Logout, Profile, Register, Start, SwitchItem } from './auth.action';
import { AuthStateModel } from './auth.model';

@State<AuthStateModel>({
  name: 'auth',
  defaults: {
    installation_id: '',
    session_id: '',
    token: '',
    tokenExchangeMap: ''
  }
})
@Injectable()
export class AuthState {
  constructor(private api: ApiService, private storage: StorageService, private store: Store) { }

  @Action(Register)
  register({ patchState, dispatch }: StateContext<AuthStateModel>, { params }: Register) {
    const { obSSOCookie } = params;

    this.storage.cookie.delete(COOKIE_SESSION);
    this.storage.cookie.delete(COOKIE_OBSSO);

    return this.api.auth.device(params).pipe(
      switchMap((data) => {
        const { installationId } = data;
        const { deviceVendor, deviceModel, deviceIsTablet, appId, appPlatform, appVersion } = params;

        this.storage.cookie.push(
          COOKIE_OBSSO,
          encodeCookieNew({
            obSSOCookie
          })
        );

        this.storage.cookie.push(
          COOKIE_SESSION,
          encodeCookie({
            installation_id: installationId
          })
        );

        this.storage.cookie.push(
          COOKIE_DEV,
          encodeCookie({
            installationId, // device
            deviceVendor, // device
            deviceModel, // device
            deviceIsTablet, // device
            appId, // device
            appPlatform, // device
            appVersion // device
          })
        );

        patchState({
          installation_id: installationId
        });

        return dispatch(new Start());
      })
    );
  }

  @Action(Start)
  start({ patchState, getState }: StateContext<AuthStateModel>) {
    const { installation_id } = getState();

    return this.api.auth.cookieAuth(false).pipe(
      tap((data) => {
        patchState(this.setTokenState({ ...data, installation_id }));
      })
    );
  }

  @Action(Login)
  login({ dispatch, patchState, getState }: StateContext<AuthStateModel>, { params, register }: Login) {
    const { username, password, obSSOCookie } = params;

    return dispatch(new Register({ register, obSSOCookie })).pipe(
      switchMap((state: any) => {
        const {
          session: {
            auth: { session_id }
          }
        } = state;
        return dispatch(new Profile());
      })
    );
  }

  @Action(Profile)
  profile({ patchState }: StateContext<AuthStateModel>) {
    return this.api.auth.profile().pipe(
      tap((data) => {
        const {
          email,
          username,
          delegationCustCode: custCode,
          profileIdentifier: profile,
          givenName,
          familyName,
          name,
          customerType: type,
          isEnriched,
          customerId: customerCodePerson,
          company,
          billingObjects
        } = data;

        const sims = billingObjects?.map((i) => ({ value: i.value })) || null;

        // eslint-disable-next-line
      const userTypeMapping = (profile) => {
          switch (profile) {
          case 'BUSINESS_MASTER_REFERENT':
          case 'BUSINESS_OPERATIVE_REFERENT':
            return 'chooser';
          case 'BUSINESS_EMPLOYEE':
            return 'enduser';
          default:
            return 'anonymous';
          }
        };

        this.storage.cookie.push(
          COOKIE_DEV,
          encodeCookie({
            ...decodeCookie(this.storage.cookie.pull(COOKIE_DEV) as string),

            items: sims?.map((d) => d.value).join(',') || null,

            username,
            email,
            custCode,

            segment: company?.segment,

            userType: userTypeMapping(profile),

            name: givenName || name,
            surname: familyName,

            isDunning: company?.isDunning ?? false,
            isHotline: company?.isHotline ?? false,

            walletId: null,

            code: company?.segment,
            customerCodeCompany: custCode,
            customerCodePerson,
            type,
            isEnriched,
            profile
          })
        );

        patchState({ sims } as any);
      })
    );
  }

  @Action(SwitchItem)
  switchItem({ dispatch }: StateContext<AuthStateModel>, { item }: SwitchItem) {
    const { value: msisdn } = item;

    return this.api.auth.boDetails(msisdn).pipe(
      tap((data) => {
        const {
          msisdnDetails: { simId: id, hashedMsisdn: pbo, isDual: dual, isUserPlus: userPlus, ptCode: code },
          customerDetails: { custcode40: customerCodeCompany },
        } = data;

        const cookieDev = decodeCookie(this.storage.cookie.pull(COOKIE_DEV) as string);
        const isMyMsisdn = !!cookieDev['items']?.split(',').find((d) => d === msisdn);

        this.storage.cookie.push(
          COOKIE_DEV,
          encodeCookie({
            ...cookieDev,
            id, // currentItem
            msisdn, // currentItem
            isMyMsisdn, // currentItem
            pbo, // currentItem
            dual, // currentItem
            customerCodeCompany, // currentItem
            userPlus, // currentItem
            code // currentItem
          })
        );
      })
    );
  }

  @Action(Logout)
  logout() { }

  private setTokenState(data: any): any {
    const token_map = this.extractHeaders(data);
    const token = data.oldDxlToken.accessToken;
    const installation_id = 'test';
    const token_exchange_map = serializeFromObjectToString(token_map);

    const parseJwt = (token: string) => {
      const base64Url = token?.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map(function (c) {
            return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
          })
          .join('')
      );
      return JSON.parse(jsonPayload);
    };
    const session_id = parseJwt(token).session_id;
    // TODO: prendersi anche il token_exchange_map: tokenExchangeMap
    this.storage.cookie.push(
      COOKIE_SESSION,
      encodeCookie({
        session_id: parseJwt(token)?.session_id,
        installation_id,
        secret_key: '',
        mid: '',
        token,
        tokenExchangeMap: token_exchange_map
      })
    );

    // TODO check if auth must be in this model
    this.storage.cookie.push(
      COOKIE_DEV,
      encodeCookie({
        ...decodeCookie(this.storage.cookie.pull(COOKIE_DEV) as string),
        sessionId: session_id, // auth
        token, // auth
        tokenExchangeMap: token_exchange_map// auth
      })
    );


    localStorage.setItem('tokenExchangeMap', JSON.stringify(token_map));


    return {
      session_id,
      token,
      tokenExchangeMap: { ...token_map }
    };
  }

  extractHeaders(obj:any) {
    const result = {};
    if (obj) {
      for (const key in obj) {
        if (key !== 'userInfo' && key !== 'installation_id' && obj[key].hasOwnProperty('header') && obj[key].hasOwnProperty('accessToken')) {
          result[obj[key].header] = obj[key].accessToken;
        }
      }
    }


    return result;
  }
}
